
import { useState, useCallback, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';

export type UserProfile = Database["public"]["Tables"]["profiles"]["Row"] | null;

/**
 * Hook for managing user profile data
 * @param user The current authenticated user
 */
export const useProfile = (user: User | null) => {
  const [profile, setProfile] = useState<UserProfile>(null);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Fetch user profile data from Supabase
   */
  const fetchUserProfile = useCallback(async (userId: string) => {
    if (!userId) return;

    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("profiles")
        .select()
        .eq("id", userId)
        .single();
        
      if (error) throw error;
      setProfile(data);
    } catch (error) {
      console.error("Error fetching user profile:", error);
      setProfile(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch profile when user changes
  useEffect(() => {
    if (user?.id) {
      fetchUserProfile(user.id);
    } else {
      setProfile(null);
    }
  }, [user, fetchUserProfile]);

  // Determine if the user is an admin
  const isAdmin = profile?.role === "admin";

  return {
    profile,
    isAdmin,
    isLoading,
    fetchUserProfile
  };
};
